import Axios, { AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';

import { BrowserAccessTokenStorage } from '~/ApiClient';
import appConfig from '~/config';

type RequestConfig<T extends InternalAxiosRequestConfig | AxiosRequestConfig> = T & {
	skipTokenInjection?: boolean;
};

const axiosInstance = Axios.create();

// Serialize array query params (e.g. `ids`, `rs`) as repeated plain keys (`ids=a&ids=b`)
// instead of axios' default bracketed form (`ids[]=a&ids[]=b`), to match the backend's
// expected `@RequestParam List<...>` binding (which does not understand `[]` suffixes).
axiosInstance.defaults.paramsSerializer = { indexes: null };

export const apiClientMutator = <T>(
	config: AxiosRequestConfig,
	options?: RequestConfig<AxiosRequestConfig>
): Promise<T> => {
	const baseUrl = process.env.API_BASE_URL ?? appConfig.api.baseUrl;

	axiosInstance.defaults.baseURL = baseUrl;
	axiosInstance.defaults.headers.post['Content-Type'] = 'application/json';
	axiosInstance.defaults.headers.put['Content-Type'] = 'application/json';
	axiosInstance.defaults.headers.delete['Content-Type'] = 'application/json';

	return axiosInstance({ ...config, ...options }).then(({ data }) => data);
};

const getAccessToken = () => {
	let accessToken: string | undefined;

	if (!accessToken) {
		const token = new BrowserAccessTokenStorage('access-token', {
			path: '/',
			httpOnly: false,
		});

		accessToken = token.loadToken() ?? undefined;
	}

	return accessToken;
};

const requestIntercept = async (request: RequestConfig<InternalAxiosRequestConfig>) => {
	if (!request.skipTokenInjection) {
		const token = getAccessToken();
		if (token && request.headers) {
			request.headers['Authorization'] = `Bearer ${token}`;
		}
	}

	// Fix for bug https://github.com/axios/axios/issues/89#issuecomment-346650868
	request.data = request?.data ?? {};

	return request;
};

axiosInstance.interceptors.request.use(requestIntercept, (error) => Promise.reject(error));

export type ErrorType<Error> = AxiosError<Error>;
